import { Box, Divider, Skeleton, Stack, useColorScheme } from "@mui/joy";
import { Plus } from "lucide-react";

import { InitialInspection, EstimateContract } from "../../../";
import { ContractTaskComponent } from "../../../ContractSigning/components";
import { MaterialOrderingTaskComponent } from "../../../MaterialOrdering/components";
import { JobExecutionTaskComponent } from "../../..//JobExecution/components";
import { FinalSettlementTaskComponent } from "../../../FinalSettlement/components";
import { BuildingPermitTaskComponent } from "../../../BuildingPermit/component";
import { Button, ProgressStepper, Alert } from "../../../../shared";
import { useResponsive } from "../../../../hooks";

import { useTaskHandler } from "./hooks";

export default function ListTask() {
    const {
        currentStep,
        taskList,
        handleClickOnProgressBar,
        activeTask,
        listTasksByProjectSvc,
        markTaskCompleteAndProceed,
    } = useTaskHandler();

    const { mode } = useColorScheme();

    const isLoading = listTasksByProjectSvc.isPending && taskList.length === 0;

    const { isMobile } = useResponsive();

    return (
        <Box mt={-2} mx={0}>
            <Box
                sx={{
                    position: "sticky",
                    top: "48px",
                    width: "100%",
                    zIndex: 1002,
                    bgcolor: mode === "light" ? "white" : "#1e1e1e",
                    pt: 1,
                }}
            >
                <Box
                    mb={{ xs: 1, md: 2 }}
                    mt={{ xs: 0, md: 1 }}
                    px={{ xs: 2, sm: 3 }}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    gap={2}
                >
                    {isLoading ? (
                        <Skeleton
                            variant="rectangular"
                            width="80%"
                            height={37}
                            sx={{ borderRadius: "30px" }}
                        />
                    ) : (
                        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                            <ProgressStepper
                                currentStep={currentStep}
                                steps={taskList}
                                onClick={handleClickOnProgressBar}
                            />
                        </Box>
                    )}
                    <Divider orientation="vertical" />
                    {isLoading ? (
                        <Skeleton
                            variant="rectangular"
                            width={100}
                            height={37}
                            sx={{ borderRadius: "md" }}
                        />
                    ) : isMobile ? null : (
                        <Button
                            variant={"outlined"}
                            color={"neutral"}
                            startDecorator={<Plus size={14} />}
                            sx={{ flexShrink: 0 }}
                        >
                            Add task
                        </Button>
                    )}
                </Box>
                <Divider inset="context" />
            </Box>

            {listTasksByProjectSvc.isError && (
                <Box mt={2}>
                    <Alert
                        errorMessage={
                            (listTasksByProjectSvc.error as any)?.message ||
                            "Failed to load project tasks"
                        }
                    />
                </Box>
            )}

            <Box display={"flex"} gap={2} flexDirection={"column"} mt={2}>
                {isLoading ? (
                    <Stack spacing={4} sx={{ mt: 3, px: { xs: 2, sm: 3 } }}>
                        <Box display="flex" justifyContent="space-between">
                            <Skeleton variant="text" width="30%" height={32} />
                            <Skeleton
                                variant="rectangular"
                                width={120}
                                height={36}
                                sx={{ borderRadius: "md" }}
                            />
                        </Box>
                        <Stack spacing={2}>
                            {[...Array(5)].map((_, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        display: "flex",
                                        gap: 2,
                                        alignItems: "center",
                                        py: 1,
                                    }}
                                >
                                    <Skeleton
                                        variant="circular"
                                        width={24}
                                        height={24}
                                    />
                                    <Skeleton variant="text" width="70%" />
                                    <Skeleton
                                        variant="rectangular"
                                        width={60}
                                        height={20}
                                        sx={{ borderRadius: "xl" }}
                                    />
                                </Box>
                            ))}
                        </Stack>
                    </Stack>
                ) : (
                    <>
                        {activeTask?.Name === "Initial Inspection" && (
                            <InitialInspection
                                activeTask={activeTask}
                                onComplete={() =>
                                    markTaskCompleteAndProceed(activeTask?.Id)
                                }
                            />
                        )}
                        {activeTask?.Name === "Estimate/Contract" && (
                            <EstimateContract
                                activeTask={activeTask}
                                onComplete={() =>
                                    markTaskCompleteAndProceed(activeTask?.Id)
                                }
                            />
                        )}
                        {activeTask?.Name === "Contract Signing" && (
                            <ContractTaskComponent
                                activeTask={activeTask}
                                onComplete={() =>
                                    markTaskCompleteAndProceed(activeTask?.Id)
                                }
                                actionProgress={{}}
                            />
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
// force recompile
